import React from 'react';
import { useAppContext } from '../../context/appContext';
import Icon from '../Icon/Icon';

type ModalProps = {
  onClose?: () => void;
  onHide?: () => void;
  show: boolean;
  children: React.ReactNode;
};

function Modal({ children, show, onClose, onHide }: ModalProps) {
  const [, dispatch] = useAppContext();
  const elementRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function listener(event: MouseEvent | TouchEvent) {
      if (elementRef?.current) {
        const element = elementRef.current;
        if (element && element.contains(event.target as Node)) {
          return;
        }
        onHide?.();
      }
    }

    if (show && onHide) {
      document.addEventListener('mousedown', listener);
    }

    return () => {
      document.removeEventListener('mousedown', listener);
    };
  }, [show, onHide]);

  React.useEffect(() => {
    dispatch({ type: 'SET_MODAL_STATE', payload: 'OPENED' });

    return () => {
      dispatch({ type: 'SET_MODAL_STATE', payload: 'CLOSED' });
    };
  }, [show, dispatch]);

  return (
    <div
      className={`${show ? 'block' : 'hidden'} fixed inset-0 z-50 overflow-y-auto`}
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex tablet:block items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center tablet:p-0">
        <div
          className="fixed inset-0 bg-[#303030] bg-opacity-75 transition-opacity"
          aria-hidden="true"
        />

        <span
          className="hidden tablet:inline-block tablet:align-middle tablet:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div
          ref={elementRef}
          className="mobile:w-full relative inline-block align-bottom text-left overflow-hidden transform transition-all
            tablet:my-8 tablet:align-middle tablet:max-w-[800px] tablet:w-fit"
        >
          {onClose && (
            <div className="w-full h-9 mb-7">
              <button
                type="button"
                onClick={onClose}
                className="w-9 h-9 bg-white rounded-full flex items-center justify-center float-right cursor-pointer"
              >
                <Icon id="close" className="w-3 h-3" />
              </button>
            </div>
          )}
          <div className="w-full rounded-xl bg-white shadow-xl">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default Modal;
